import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Chip,
  IconButton,
  Button,
  Tooltip,
  InputAdornment,
  Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoIcon from '@mui/icons-material/Info';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/**
 * OmopSuggestionsTable Component
 * Displays AI-generated OMOP concept suggestions in a sortable, searchable table
 * Allows users to pin suggestions and submit change proposals inline
 *
 * @param {object} props
 * @param {array} props.candidates - Array of AI-generated concept candidates
 * @param {string} props.currentTopMatchId - Concept ID of currently selected top match
 * @param {array} props.pinnedConceptIds - Array of user-pinned concept IDs
 * @param {function} props.onSelectCandidate - Callback when user selects a candidate (direct change for Data Stewards)
 * @param {function} props.onSubmitProposal - Callback when user submits a change proposal ({ candidate, justification })
 * @param {function} props.onTogglePin - Callback when user pins/unpins a candidate
 * @param {boolean} props.isProcessing - Whether an action is in progress
 * @param {string} props.searchQuery - External search query (optional)
 * @param {function} props.onSearchChange - Callback when search changes (optional)
 */
const OmopSuggestionsTable = ({
  candidates = [],
  currentTopMatchId,
  pinnedConceptIds = [],
  onSelectCandidate,
  onSubmitProposal,
  onTogglePin,
  isProcessing = false,
  searchQuery: externalSearchQuery,
  onSearchChange
}) => {
  // State for table controls
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const searchQuery = externalSearchQuery !== undefined ? externalSearchQuery : internalSearchQuery;
  const [orderBy, setOrderBy] = useState('score');
  const [order, setOrder] = useState('desc');
  const [expandedRow, setExpandedRow] = useState(null);

  // State for proposal form
  const [expandedProposalRow, setExpandedProposalRow] = useState(null);
  const [proposalJustification, setProposalJustification] = useState('');

  const handleSearchChange = (value) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearchQuery(value);
    }
  };

  // Filter candidates based on search query
  const filteredCandidates = useMemo(() => {
    if (!searchQuery.trim()) return candidates;

    const query = searchQuery.toLowerCase();
    return candidates.filter(candidate =>
      candidate.conceptName?.toLowerCase().includes(query) ||
      candidate.conceptId?.toString().includes(query) ||
      candidate.conceptCode?.toLowerCase().includes(query) ||
      candidate.vocabulary?.toLowerCase().includes(query)
    );
  }, [candidates, searchQuery]);

  // Sort candidates
  const sortedCandidates = useMemo(() => {
    const sorted = [...filteredCandidates];

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case 'rank':
          aValue = a.rank || 999;
          bValue = b.rank || 999;
          break;
        case 'score':
          aValue = a.score || 0;
          bValue = b.score || 0;
          break;
        case 'name':
          aValue = a.conceptName || '';
          bValue = b.conceptName || '';
          break;
        case 'vocabulary':
          aValue = a.vocabulary || '';
          bValue = b.vocabulary || '';
          break;
        case 'domain':
          aValue = a.domain || '';
          bValue = b.domain || '';
          break;
        default:
          return 0;
      }

      // Handle string vs number comparison
      if (typeof aValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return order === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }
    });

    return sorted;
  }, [filteredCandidates, orderBy, order]);

  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Handle row expand
  const handleExpandRow = (conceptId) => {
    setExpandedRow(expandedRow === conceptId ? null : conceptId);
  };

  // Check if concept is pinned
  const isPinned = (conceptId) => {
    return pinnedConceptIds.includes(conceptId);
  };

  // Check if concept is current top match
  const isCurrentTopMatch = (conceptId) => {
    return conceptId === currentTopMatchId;
  };

  // Get score color
  const getScoreColor = (score) => {
    if (score >= 0.9) return 'success.main';
    if (score >= 0.7) return 'warning.main';
    return 'text.secondary';
  };

  // Handle proposal form open/close
  const handleOpenProposalForm = (conceptId) => {
    setExpandedProposalRow(conceptId);
    setProposalJustification('');
  };

  const handleCloseProposalForm = () => {
    setExpandedProposalRow(null);
    setProposalJustification('');
  };

  // Handle proposal submission
  const handleSubmitProposal = (candidate) => {
    if (onSubmitProposal) {
      onSubmitProposal({
        candidate,
        justification: proposalJustification
      });
    }

    handleCloseProposalForm();
  };

  if (candidates.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          Keine KI-Vorschläge verfügbar
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Results Count */}
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          {filteredCandidates.length} von {candidates.length} Vorschlägen
          {pinnedConceptIds.length > 0 && ` • ${pinnedConceptIds.length} gespeichert`}
        </Typography>
        {searchQuery && (
          <Button size="small" onClick={() => handleSearchChange('')}>
            Filter zurücksetzen
          </Button>
        )}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell width={40} />
              <TableCell width={60}>
                <TableSortLabel
                  active={orderBy === 'rank'}
                  direction={orderBy === 'rank' ? order : 'asc'}
                  onClick={() => handleRequestSort('rank')}
                >
                  Rank
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Concept Name
                </TableSortLabel>
              </TableCell>
              <TableCell width={120}>Concept ID</TableCell>
              <TableCell width={100}>
                <TableSortLabel
                  active={orderBy === 'vocabulary'}
                  direction={orderBy === 'vocabulary' ? order : 'asc'}
                  onClick={() => handleRequestSort('vocabulary')}
                >
                  Vocabulary
                </TableSortLabel>
              </TableCell>
              <TableCell width={120}>
                <TableSortLabel
                  active={orderBy === 'domain'}
                  direction={orderBy === 'domain' ? order : 'asc'}
                  onClick={() => handleRequestSort('domain')}
                >
                  Domain
                </TableSortLabel>
              </TableCell>
              <TableCell width={80} align="center">
                <TableSortLabel
                  active={orderBy === 'score'}
                  direction={orderBy === 'score' ? order : 'asc'}
                  onClick={() => handleRequestSort('score')}
                >
                  Score
                </TableSortLabel>
              </TableCell>
              <TableCell width={50} align="center">
                <Tooltip title="ATHENA">
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    borderRadius: 1,
                    p: 0.5,
                    width: 24,
                    height: 24
                  }}>
                    <Box
                      component="img"
                      src="/athena-favicon.ico"
                      alt="ATHENA"
                      sx={{ width: 14, height: 14 }}
                    />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell width={60} align="center">Pin</TableCell>
              <TableCell width={120} align="center">Aktion</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCandidates.map((candidate, idx) => {
              const isTop = isCurrentTopMatch(candidate.conceptId);
              const pinned = isPinned(candidate.conceptId);
              const isExpanded = expandedRow === candidate.conceptId;
              const hasReason = candidate.reason && candidate.reason.trim().length > 0;

              return (
                <React.Fragment key={candidate.conceptId || idx}>
                  <TableRow
                    sx={{
                      backgroundColor: isTop ? '#e8f5e9' : pinned ? '#fff3e0' : 'inherit',
                      '&:hover': {
                        backgroundColor: isTop ? '#c8e6c9' : pinned ? '#ffe0b2' : '#f5f5f5'
                      }
                    }}
                  >
                    {/* Expand Button */}
                    <TableCell>
                      {hasReason && (
                        <IconButton
                          size="small"
                          onClick={() => handleExpandRow(candidate.conceptId)}
                        >
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      )}
                    </TableCell>

                    {/* Rank */}
                    <TableCell>
                      <Chip
                        label={`#${candidate.rank || (idx + 1)}`}
                        size="small"
                        color={isTop ? "success" : "default"}
                        variant={isTop ? "filled" : "outlined"}
                      />
                    </TableCell>

                    {/* Concept Name */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={isTop ? "bold" : "regular"}>
                          {candidate.conceptName}
                        </Typography>
                        {isTop && (
                          <Chip label="Aktuell" size="small" color="success" sx={{ height: 20 }} />
                        )}
                      </Box>
                    </TableCell>

                    {/* Concept ID */}
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {candidate.conceptId}
                      </Typography>
                    </TableCell>

                    {/* Vocabulary */}
                    <TableCell>
                      <Chip label={candidate.vocabulary} size="small" variant="outlined" />
                    </TableCell>

                    {/* Domain */}
                    <TableCell>
                      <Typography variant="body2">
                        {candidate.domain || 'Measurement'}
                      </Typography>
                    </TableCell>

                    {/* Score */}
                    <TableCell align="center">
                      <Tooltip title={`KI-Konfidenz: ${Math.round((candidate.score || 0) * 100)}%`}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ color: getScoreColor(candidate.score || 0) }}
                        >
                          {Math.round((candidate.score || 0) * 100)}%
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* ATHENA Link */}
                    <TableCell align="center">
                      <Tooltip title="In ATHENA anzeigen">
                        <IconButton
                          size="small"
                          component="a"
                          href={`https://athena.ohdsi.org/search-terms/terms/${candidate.conceptId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>

                    {/* Pin Button */}
                    <TableCell align="center">
                      <Tooltip title={pinned ? "Nicht mehr speichern" : "Vorschlag speichern"}>
                        <IconButton
                          size="small"
                          onClick={() => onTogglePin && onTogglePin(candidate)}
                          disabled={isProcessing}
                          color={pinned ? "primary" : "default"}
                        >
                          {pinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>

                    {/* Action Button */}
                    <TableCell align="center">
                      {isTop ? (
                        <Chip
                          label="In Verwendung"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckIcon />}
                          onClick={() => handleOpenProposalForm(candidate.conceptId)}
                          disabled={isProcessing}
                        >
                          Vorschlagen
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expanded Row - Proposal Form */}
                  {expandedProposalRow === candidate.conceptId && (
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                        <Collapse in={true} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 3, backgroundColor: '#f9f9f9', border: '2px solid #1976d2', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Vorschlag: {candidate.conceptName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                              {candidate.vocabulary} {candidate.conceptCode} als alternatives Mapping vorschlagen
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                              {/* Justification TextField */}
                              <TextField
                                size="small"
                                placeholder="Begründung (optional): Warum ist dieses Concept besser geeignet?"
                                value={proposalJustification}
                                onChange={(e) => setProposalJustification(e.target.value)}
                                multiline
                                rows={2}
                                sx={{ flexGrow: 1 }}
                              />

                              {/* Action Buttons */}
                              <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleSubmitProposal(candidate)}
                                >
                                  Einreichen
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={handleCloseProposalForm}
                                >
                                  Abbrechen
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Expanded Row - Show Reason */}
                  {hasReason && (
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, pl: 6, pr: 2, backgroundColor: '#fafafa' }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                              <InfoIcon fontSize="small" color="primary" sx={{ mt: 0.3 }} />
                              <Box>
                                <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                                  💡 Warum dieser Vorschlag?
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {candidate.reason}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredCandidates.length === 0 && (
        <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            Keine Ergebnisse für "{searchQuery}"
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default OmopSuggestionsTable;
